import { Link } from 'react-router-dom';
import type { SearchResult } from '@/lib/types';
import { Badge } from '@/components/ui/badge';
import { Globe, Search, User } from 'lucide-react';


interface HistoryItemProps {
  search: SearchResult;
}

export function HistoryItem({ search }: HistoryItemProps) {
  const getResultUrl = () => {
    const routes: Record<string, string> = {
      'search-anything': `/search/anything/result?id=${search.id}`,
      'search-by-photo': `/search/photo/result?id=${search.id}`,
      'person-search': `/search/person/result?id=${search.id}`,
    };
    return routes[search.type] || '#';
  };

  const getIcon = () => {
    switch (search.type) {
      case 'search-anything':
        return <Globe className="w-6 h-6 text-blue-600" />;
      case 'person-search':
        return <User className="w-6 h-6 text-green-600" />;
      case 'search-by-photo':
      default:
        return <Search className="w-6 h-6 text-gray-600" />;
    }
  };

  const getStatusColor = () => {
    switch (search.status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'analyzing':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Link to={getResultUrl()}>
      <div className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
        {/* Icon/Thumbnail */}
        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center text-xl flex-shrink-0">
          {getIcon()}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900 truncate">{search.query}</h3>
          {search.status === 'analyzing' && (
            <p className="text-sm text-blue-600">Researching Now...</p>
          )}
        </div>

        {/* Progress Badge */}
        {search.status === 'completed' && (
          <Badge className={getStatusColor()}>
            {search.progress}
          </Badge>
        )}
      </div>
    </Link>
  );
}
